/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { AdaptiveFormShell, Form } from '@/shared/ui/components/form';
import { documentMask, phoneMask } from '@/shared/utils/input-masks';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { ProfileField, ProfileDrawerSubmitButton } from './profile-drawer-parts';
import { useSaveAgencyProfile } from './use-save-agency-profile';
import {
  documentLabelKey, documentPatch, registrationNameKey, storedDocument,
} from './registration-document';

/**
 * Builds the zod validation schema for the agency registration form.
 * @param t - Translation function used to localize validation messages.
 * @returns A zod object schema describing the registration fields.
 */
function createRegistrationSchema(t: (key: string) => string) {
  return z.object({
    name: z.string().min(1, t('agencyData.validation.nomeObrigatorio')),
    document: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().email(t('agencyData.validation.invalidEmail')).or(z.literal('')).optional(),
    site: z.string().optional(),
    creci: z.string().optional(),
  });
}

type RegistrationFormData = {
  name: string;
  document?: string;
  phone?: string;
  email?: string;
  site?: string;
  creci?: string;
};

/**
 * Drawer form for editing the agency's core registration data
 * (name, CNPJ, phone, email, site, CRECI).
 * @param props - Component props.
 * @param props.open - Whether the drawer is visible.
 * @param props.onClose - Callback invoked to dismiss the drawer.
 * @returns The registration edit drawer element.
 */
export function RegistrationDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((s) => s);
  const save = useSaveAgencyProfile(onClose);

  const values: RegistrationFormData = {
    name: agency?.name ?? '',
    document: storedDocument(agency ?? {}),
    phone: agency?.phone ?? '',
    email: agency?.email ?? '',
    site: agency?.site ?? '',
    creci: agency?.creci ?? '',
  };

  const {
    control, handleSubmit, reset,
    formState: { isSubmitting },
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(createRegistrationSchema(t)),
    values,
  });

  // Discards edits abandoned on a previous open, and picks up the agency
  // record when it arrives after this drawer has already mounted.
  useEffect(() => {
    if (open) reset(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agency?.id]);

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      density="compact"
      title={t('v2.profile.editProfile')}
      footer={
        <ProfileDrawerSubmitButton
          form="registration-form"
          disabled={isSubmitting}
          label={t('v2.profile.save')}
          savingLabel={t('v2.profile.saving')}
        />
      }
    >
      <Form
        id="registration-form"
        onSubmit={handleSubmit((data) => save({
          name: data.name,
          phone: data.phone,
          email: data.email,
          site: data.site,
          creci: data.creci,
          ...documentPatch(data.document),
        }))}
        className="grid grid-cols-1 md:grid-cols-2 gap-(--space-4)"
      >
        <div className="col-span-full">
          <ProfileField
            control={control}
            name="name"
            label={t(registrationNameKey(agency?.type))}
            required
          />
        </div>
        <ProfileField
          control={control}
          name="document"
          label={t(documentLabelKey(agency ?? {}))}
          mask={documentMask}
        />
        <ProfileField
          control={control}
          name="phone"
          label={t('agencyData.labels.telefone')}
          mask={phoneMask}
        />
        <ProfileField control={control} name="email" label={t('v2.profile.email')} type="email" />
        <ProfileField control={control} name="site" label={t('agencyData.labels.site')} />
        <ProfileField control={control} name="creci" label={t('v2.profile.creci')} />
      </Form>
    </AdaptiveFormShell>
  );
}
